import axios from "axios";
import API_CONFIG from "../config/api.config";

const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use(cfg => {
  const token = localStorage.getItem("at_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

api.interceptors.response.use(
  r => r,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem("at_token");
      localStorage.removeItem("at_user");
      window.dispatchEvent(new Event("at_logout"));
    }
    return Promise.reject(err);
  }
);

const url = (tpl, id) => tpl.replace(":id", String(id));

// ── Auth ──────────────────────────────────────────────────────
export const authApi = {
  login: (body) => api.post(API_CONFIG.AUTH.LOGIN, body),
  logout: () => Promise.resolve({ status: true }),
};

// ── Agents ────────────────────────────────────────────────────
export const agentApi = {
  // GET /user/list
  list: () => api.get(API_CONFIG.AGENTS.LIST),

  // POST /users
  // body: { fullName, email, mobile, password, city, pincode, role, alternateMobile, fcmToken, latitude, longitude, address }
  create: (data) => api.post(API_CONFIG.AGENTS.CREATE, {
    fullName:        data.fullName,
    email:           data.email,
    mobile:          data.mobile,
    password:        data.password,
    city:            data.city            || "",
    pincode:         data.pincode         || "",
    role:            data.role            || "AGENT",
    alternateMobile: data.alternateMobile || "",
    fcmToken:        "web_admin_token",
    latitude:        data.latitude        || "",
    longitude:       data.longitude       || "",
    address:         data.address         || "",
  }),

  // PATCH /users/:id  (profile — no password/status)
  update: (id, data) => api.patch(url(API_CONFIG.AGENTS.UPDATE, id), {
    fullName:        data.fullName,
    city:            data.city            || "",
    pincode:         data.pincode         || "",
    role:            data.role            || "AGENT",
    alternateMobile: data.alternateMobile || "",
    fcmToken:        "web_admin_token",
    latitude:        data.latitude        || "",
    longitude:       data.longitude       || "",
    address:         data.address         || "",
  }),

  // PATCH /users/:id/password  → { password }
  updatePassword: (id, password) =>
    api.patch(url(API_CONFIG.AGENTS.UPDATE_PASSWORD, id), { password }),

  // PATCH /users/:id/status  → { status: "on" | "off" }
  updateStatus: (id, status) =>
    api.patch(url(API_CONFIG.AGENTS.UPDATE_STATUS, id), { status }),

  // PATCH /users/:id/location-permission  → { allowAnyLocation: 0 | 1 }
  updateLocationPerm: (id, allowAnyLocation) =>
    api.patch(url(API_CONFIG.AGENTS.UPDATE_LOCATION_PERM, id), { allowAnyLocation }),
};

// ── Live Tracking ─────────────────────────────────────────────
export const trackingApi = {
  allLive:    ()   => api.get(API_CONFIG.TRACKING.ALL_LIVE),
  singleLive: (id) => api.get(url(API_CONFIG.TRACKING.SINGLE_LIVE, id)),
};

// ── GPS History ───────────────────────────────────────────────
export const historyApi = {
  getHistory: (agentId, fromDateTime, toDateTime) =>
    api.post(API_CONFIG.HISTORY.GPS_HISTORY, { agentId, fromDateTime, toDateTime }),
};

// ── Alerts ────────────────────────────────────────────────────
export const alertApi = {
  send:   (data) => api.post(API_CONFIG.ALERTS.SEND, data),
  list:   (p)    => api.get(API_CONFIG.ALERTS.LIST, { params: p }),
  cancel: (id)   => api.patch(url(API_CONFIG.ALERTS.CANCEL, id)),
};

// ── Zones ─────────────────────────────────────────────────────
export const zoneApi = {
  list:   ()          => api.get(API_CONFIG.ZONES.LIST),
  create: (data)      => api.post(API_CONFIG.ZONES.CREATE, data),
  update: (id, data)  => api.put(url(API_CONFIG.ZONES.UPDATE, id), data),
  delete: (id)        => api.delete(url(API_CONFIG.ZONES.DELETE, id)),
};

// ── Products ───────────────────────────────────────────────────
export const productsApi = {
  list:   ()          => api.get(API_CONFIG.PRODUCTS.LIST),
  create: (data)      => api.post(API_CONFIG.PRODUCTS.CREATE, data),
  update: (id, data)  => api.put(url(API_CONFIG.PRODUCTS.UPDATE, id), data),
  detail: (id)        => api.get(url(API_CONFIG.PRODUCTS.DETAIL, id)),
};

// ── Users ──────────────────────────────────────────────────────
export const usersApi = {
  list:   (params)    => api.get(API_CONFIG.USERS.LIST, { params }),
  create: (data)      => api.post(API_CONFIG.USERS.CREATE, data),
  update: (id, data)  => api.patch(url(API_CONFIG.USERS.UPDATE, id), data),
  detail: (id)        => api.get(url(API_CONFIG.USERS.DETAIL, id)),
  agentHomeLocation: (id) => api.get(url(API_CONFIG.USERS.AGENT_HOME_LOCATION, id)),
  agentsMeta: ()      => api.get(API_CONFIG.USERS.AGENTS_META),
  clearDevice: (id)   => api.patch(url(API_CONFIG.USERS.CLEAR_DEVICE, id)),
  loginWindow: (id)   => api.get(url(API_CONFIG.USERS.LOGIN_WINDOW, id)),
  // allowRelogin: 1 grants a one-time extra login after the first session, 0 revokes it.
  setLoginPermission: (id, allowRelogin) =>
    api.patch(url(API_CONFIG.USERS.LOGIN_PERMISSION, id), { allowRelogin }),
};

// ── External Users ─────────────────────────────────────────────
export const externalUsersApi = {
  list:   (params)    => api.get(API_CONFIG.EXTERNAL_USERS.LIST, { params }),
  create: (data)      => api.post(API_CONFIG.EXTERNAL_USERS.CREATE, data),
  update: (id, data)  => api.patch(url(API_CONFIG.EXTERNAL_USERS.UPDATE, id), data),
  detail: (id)        => api.get(url(API_CONFIG.EXTERNAL_USERS.DETAIL, id)),
  delete: (id)        => api.delete(url(API_CONFIG.EXTERNAL_USERS.DELETE, id)),
};

// ── Reports ────────────────────────────────────────────────────
export const reportsApi = {
  amountCollections: (params) => api.get(API_CONFIG.REPORTS.AMOUNT_COLLECTIONS, { params }),
  agentActivity: (params) => api.get(API_CONFIG.REPORTS.AGENT_ACTIVITY, { params }),
};

// ── Attendance ─────────────────────────────────────────────────
export const attendanceApi = {
  /** GET ?fromDate=YYYY-MM-DD&toDate=YYYY-MM-DD → { status, message, url, count } */
  agentsExcel: (fromDate, toDate, productId = "") =>
    api.get(API_CONFIG.ATTENDANCE.AGENTS_EXCEL, {
      params: {
        fromDate,
        toDate,
        ...(productId ? { productId } : {}),
      },
    }),
};

// ── App Alerts ─────────────────────────────────────────────────
export const appAlertsApi = {
  list: (params) => api.get(API_CONFIG.APP_ALERTS.LIST, { params }),
  exportCsv: (params) => api.get(API_CONFIG.APP_ALERTS.LIST, { params: { ...params, export: "csv" }, responseType: "blob" }),
};

// ── Geofence (Idle-near-home) Alerts ───────────────────────────
export const geofenceAlertsApi = {
  list: (params) => api.get(API_CONFIG.GEOFENCE_ALERTS.LIST, { params }),
  exportCsv: (params) =>
    api.get(API_CONFIG.GEOFENCE_ALERTS.LIST, {
      params: { ...params, export: "csv" },
      responseType: "blob",
      timeout: 0, // full-dataset export may take longer than the default
    }),
};

// ── App Usage ──────────────────────────────────────────────────
export const appUsageApi = {
  list: (params) => api.get(API_CONFIG.APP_USAGE.LIST, { params }),
  exportCsv: (params) =>
    api.get(API_CONFIG.APP_USAGE.LIST, {
      params: { ...params, export: "csv" },
      responseType: "blob",
      timeout: 0, // full-dataset export may take longer than the default
    }),
};

// ── Offices ────────────────────────────────────────────────────
export const officesApi = {
  list: () => api.get(API_CONFIG.OFFICES.LIST),
};

// ── App Releases (APK upload + share URL) ──────────────────────
export const appReleasesApi = {
  upload: (formData, onUploadProgress) =>
    api.post(API_CONFIG.APP_RELEASES.UPLOAD, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 0, // large APK uploads should not time out
      onUploadProgress,
    }),
  list: () => api.get(API_CONFIG.APP_RELEASES.LIST),
  latest: () => api.get(API_CONFIG.APP_RELEASES.LATEST),
};

export default api;
